import { useTranslation } from 'react-i18next';
import { Ban, Check } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { Device } from '../types';

interface ToggleStatusButtonProps {
  device: Device;
  onToggle: (id: number, status: 'ACTIVE' | 'BLOCKED') => void;
  isPending?: boolean;
}

export function ToggleStatusButton({ device, onToggle, isPending }: ToggleStatusButtonProps) {
  const { t } = useTranslation();
  const isActive = device.status === 'ACTIVE';
  const nextStatus = isActive ? 'BLOCKED' : 'ACTIVE';

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`rounded-r-none ${isActive ? 'text-red-600' : 'text-green-600'}`}
          disabled={isPending}
        >
          {isActive ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
        </Button>
      </AlertDialogTrigger>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
          <AlertDialogDescription>
            This device <strong>({device.model})</strong> will change status to{' '}
            <span className={nextStatus === 'BLOCKED' ? 'text-red-600' : 'text-green-600'}>
              {nextStatus}
            </span>
            .
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
          <AlertDialogAction
            className={`text-white ${
              nextStatus === 'BLOCKED'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
            onClick={() => onToggle(device.id, nextStatus)}
            disabled={isPending}
          >
            {nextStatus === 'BLOCKED' ? t('block') : t('activate')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
